import { Button, Flex, Text } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { UpdateVacancyBlock } from "@/components/VacancyBlocks/UpdateVacancyBlock/UpdateVacancyBlock";
import { Layout } from "@/components/Layout/Layout";
import { RootState, useAppDispatch, useAppSelector } from "@/store";
import { useEffect, useMemo } from "react";
import { fetchCustomers } from "@/store/slices/customers";
import { fetchSingleVacancy } from "@/store/slices/vacancy";
import { fetchAllProjects } from "@/store/slices/projects";
import { useUpdateVacancyForm } from "@/forms/vacancy/useVacancyForm";
import { VacancyForm } from "@/model/vacancy";
import PageLoaderWrapper from "@/components/PageLoaderWrapper";

export const UpdateVacancyPage = () => {
  const { currentVacancy, isLoading } = useAppSelector((state: RootState) => state.vacancy);

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { id } = useParams();

  useEffect(() => {
    if (id !== undefined) {
      dispatch(fetchSingleVacancy({ params: { id: Number(id) } }));
    }
  }, [dispatch, id]);

  const formik = useUpdateVacancyForm(Number(id) || 0);

  const initialVacancyValues = useMemo(() => {
    if (!currentVacancy) return null;

    return {
      name: currentVacancy.name ?? "",
      customer_id: currentVacancy.customer?.id ?? 0,
      project_id: currentVacancy.project?.id ?? 0,
      legal_entity_id: currentVacancy.legal_entity?.id ?? 0, // Нет в модели Vacancy
      profile_id: currentVacancy.profile?.id ?? 0, // Нет в модели Vacancy
      candidate_grade: currentVacancy.candidate_grade?.map((el) => String(el.id)) ?? [],
      candidate_requirements: currentVacancy.candidate_requirements ?? "",
      job_duties: currentVacancy.job_duties ?? "",
      project_description: currentVacancy.project_description ?? "",
      sales_max_offer_general: currentVacancy.sales_max_offer_general || "",
      sales_max_offer_moscow: currentVacancy.sales_max_offer_moscow || "",
      sales_max_offer_regions: currentVacancy.sales_max_offer_regions || "",
      marginality: currentVacancy.marginality ?? 0,
      purchase_rate_max: String(currentVacancy.purchase_rate_max ?? ""),
      salary_tk_max: currentVacancy.salary_tk_max ?? 0,
      salary_ip_max: currentVacancy.salary_ip_max ?? 0,
      salary_gph_max: currentVacancy.salary_gph_max ?? 0,
      required_number: currentVacancy.required_number ?? 0,
      location: currentVacancy.location ?? "",
      job_format: String(currentVacancy.job_format?.id ?? ""),
      office_address: currentVacancy.office_address ?? "",
      job_workload: String(currentVacancy.job_workload?.id ?? ""),
      attraction_period: String(currentVacancy.attraction_period?.id ?? ""),
      comments: currentVacancy.comments ?? "",
      recruiters_ids: currentVacancy.recruiters?.map((el) => el.id) ?? [],
      status: currentVacancy.status?.id ?? 0,
      attachments: [],
      attachments_delete: [],
    };
  }, [currentVacancy]);

  const hasChanges = useMemo(() => {
    if (!initialVacancyValues) return false;

    const current = formik.values;
    const initial = initialVacancyValues;

    // Проверяем файлы отдельно
    if (current.attachments.length > 0 || current.attachments_delete.length > 0) {
      return true;
    }

    // Проверяем все остальные поля автоматически
    const fieldsToCheck = Object.keys(initial).filter((key) => key !== "attachments" && key !== "attachments_delete") as Array<keyof VacancyForm>;

    return fieldsToCheck.some((field) => {
      const currentValue = current[field as keyof VacancyForm];
      const initialValue = initial[field as keyof VacancyForm];

      // Специальная обработка для массивов (candidate_grade, recruiters_ids)
      if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
        return JSON.stringify([...currentValue].sort()) !== JSON.stringify([...initialValue].sort());
      }

      return currentValue !== initialValue;
    });
  }, [formik.values, initialVacancyValues]);

  useEffect(() => {
    if (initialVacancyValues && !formik.dirty) {
      formik.resetForm({ values: initialVacancyValues });
    }
  }, [initialVacancyValues, formik.dirty]);

  useEffect(() => {
    dispatch(fetchCustomers({ params: { filters: { limit: "100000", offset: "0" } } }));
    dispatch(fetchAllProjects({ params: { filters: { limit: "100000", offset: "0" } } }));
  }, [dispatch]);

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных вакансии для редактирования...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text
            color="gray.500"
            cursor="pointer"
            onClick={() => navigate(`/vacancy/${currentVacancy?.id}`)}
            display="flex"
            alignItems="center"
            gap="8px"
          >
            <ArrowBackIcon />К вакансии
          </Text>
          <Flex flexDirection="column" gap="4px">
            <Text fontSize={24} fontWeight={500}>
              Редактирование вакансии
            </Text>
            <Text color="gray.500">Обновите информацию о вакансии</Text>
          </Flex>
          <UpdateVacancyBlock formik={formik} setIsChangedApplication={() => {}} />
          <Flex gap="16px" justifyContent="flex-end">
            <Button onClick={() => (currentVacancy ? navigate(`/vacancy/${currentVacancy.id}`) : navigate("/vacancies"))}>Отмена</Button>
            <Button
              bg="#2563eb"
              color="white"
              _hover={{ bg: "#2563eb" }}
              _active={{ bg: "#2563eb" }}
              isDisabled={!formik.isValid || !hasChanges}
              onClick={() => formik.handleSubmit()}
            >
              Сохранить изменения
            </Button>
          </Flex>
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};
